import axios from '../axios'
import { getFilenameFromContentDisposition } from '../helpers/globalUtils'
import exceptionReportApi from '../api/exceptionReportApi'
import moment from 'moment-timezone'
import auditActionsApi from '../api/auditActionsApi'
import Constants from '../constants'

export default function useApi() {
  return {
    async download(route) {
      let _response = null
      try {
        let response = await axios({
          url: route,
          method: 'GET',
          responseType: 'blob',
        })

        if (parseInt(response.status) >= 200 && parseInt(response.status) < 300) {
          const type = response.headers['content-type']
          const blob = new Blob([response.data], {
            type: type,
            encoding: 'UTF-8',
          })
          const link = document.createElement('a')
          link.href = window.URL.createObjectURL(blob)
          link.download = getFilenameFromContentDisposition(response.headers['content-disposition'])
          link.click()
          _response = response.data
        }
      } catch (error) {
        _response = error
      }

      return _response
    },
    annualBOLIReview: {
      async SaveConfiguration(data) {
        let result = await axios.post('/annual-boli-review/settings', data)

        if (result.status >= 200 && result.status < 300) {
          return result.data
        }
      },
      async getConfiguration(reportingPeriod) {
        let result = await axios.get('/annual-boli-review/' + reportingPeriod + '/settings')

        if (result.status >= 200 && result.status < 300) {
          return result
        }
      },
      async listAssetReportsInBatch(options) {
        const { take, skip, sort, desc, reportingPeriod, reportName, clientIdWithSubsidiaryCode, clientAlternateName } = options
        let _response = null

        try {
          let response = await axios.get(`/reports/${Constants.reportEnum.annualBoliReview}/${reportingPeriod}`, {
            params: {
              $top: take,
              $skip: skip,
              $orderby: `${sort} ${desc === true || (typeof desc === 'string' && desc.includes('desc')) ? 'desc' : 'asc'}`,
              $count: true,
              ...(reportName ? { reportName } : {}),
              ...(clientIdWithSubsidiaryCode ? { clientIdWithSubsidiaryCode } : {}),
              ...(clientAlternateName ? { clientAlternateName } : {}),
            },
          })

          if (parseInt(response.status) >= 200 && parseInt(response.status) < 300) {
            _response = response.data
          }
        } catch (error) {
          console.error(error)
        }

        return _response
      },
      async getInstitutionsForReportingPeriod(options) {
        const { reportingPeriod } = options
        const period = moment(reportingPeriod).format('YYYYMM')

        let _response = null

        try {
          let response = await axios.get(`/reports/${Constants.reportEnum.annualBoliReview}/${period}/unreported-institutions`)
          if (parseInt(response.status) >= 200 && parseInt(response.status) < 300) {
            _response = response.data.value
          }
        } catch (error) {
          console.error(error)
        }

        return _response
      },
      async queueReportsToGenerate(options) {
        const { period, institutions, isRegenerating } = options

        let _response = null

        try {
          let response = await axios.post('/annual-boli-review', {
            period,
            institutions,
            isRegenerating,
          })

          if (parseInt(response.status) >= 200 && parseInt(response.status) < 300) {
            _response = response.data
          }
        } catch (error) {
          console.error(error)
        }

        return _response
      },
      async deleteReport(options) {
        const { reportId } = options

        let _response = null

        try {
          let response = await axios.delete(`/annual-boli-review/0/reports/${reportId}`)

          if (parseInt(response.status) >= 200 && parseInt(response.status) < 300) {
            _response = response.data
          }
        } catch (error) {
          console.error(error)
        }

        return _response
      },
      async getAssetReportInBatchDetail(options) {
        const { batchId, reportId, reportType } = options

        let _response = null

        try {
          let response = await axios.get(`/annual-boli-review/${batchId}/reports/${reportId}?reportType=${reportType}`)

          if (parseInt(response.status) >= 200 && parseInt(response.status) < 300) {
            _response = response.data
          }
        } catch (error) {
          console.error(error)
        }

        return _response
      },
      async downloadReport(options) {
        const { batchId, reportIds, format, reportType } = options

        let _response = null

        try {
          let params = new URLSearchParams()
          if (reportIds && reportIds.length > 0) {
            params.append('reportIds', reportIds.join(','))
          }
          if (format) params.append('format', format)
          if (reportType) params.append('reportType', reportType)
          let path = `/annual-boli-review/${batchId}/download?` + params.toString()

          let response = await axios({
            url: path,
            method: 'GET',
            responseType: 'blob',
          })

          if (parseInt(response.status) >= 200 && parseInt(response.status) < 300) {
            const type = response.headers['content-type']
            const blob = new Blob([response.data], {
              type: type,
              encoding: 'UTF-8',
            })
            const link = document.createElement('a')
            link.href = window.URL.createObjectURL(blob)
            link.download = response.headers['content-disposition'].split('filename=')[1]
            link.click()
            link.remove()
            _response = response.data
          }
        } catch (error) {
          console.error(error)
        }

        return _response
      },
    },
    annualQuestionnaire: {
      async getDefaults(callback = null) {
        let result = await axios.get('/annual-questionnaire/defaults')

        if (callback) {
          callback(null, result)
          return
        }
        return result
      },
      async updateDefaults(defaultTemplateId, data) {
        let result = await axios.put(`/Questionnaires/Defaults/${defaultTemplateId}`, data)

        if (result.status >= 200 && result.status < 300) {
          return true
        }

        return false
      },
      async getClients(year, callback = null) {
        let result = await axios.get('/Questionnaires/Institutions/Missing/' + year)

        if (callback) {
          callback(null, result.data)
          return
        }
        return result.data
      },
      async getBatches(reportingPeriod = null) {
        let result = await axios.get('/Questionnaires/Reports/Years' + (reportingPeriod !== null && reportingPeriod !== '' ? '?year=' + reportingPeriod : ''), {
          query: {
            year: reportingPeriod,
          },
        })

        return result.data
      },
      async listAssetReportBatches(options, _callback) {
        const { take, skip, sort, desc, reportingPeriod } = options

        let _response = null
        let _error = null

        try {
          let response = await axios.get(`/annual-questionnaire/${reportingPeriod}?take=${take}&skip=${skip}&sort=${sort}&desc=${desc.includes('desc')}&reportingPeriod=${reportingPeriod}`)

          if (parseInt(response.status) >= 200 && parseInt(response.status) < 300) {
            _response = response.data
          }
        } catch (error) {
          _response = error
        }

        _callback(_error, _response)
      },
      async listAssetReportsInBatch(options) {
        const { batchId, take, skip, sort, desc, id, institution, status } = options

        let _response = null

        try {
          const isDesc = desc === true || (typeof desc === 'string' && desc.includes('desc'))
          let response = await axios.get(`/Questionnaires/Reports/${batchId}?$top=${take}&$skip=${skip}&status=${status}&sort=${sort}&desc=${isDesc}&accountNumber=${id}&institution=${institution}`.replaceAll('undefined', ''))

          if (parseInt(response.status) >= 200 && parseInt(response.status) < 300) {
            _response = response.data
          }
        } catch (error) {
          console.error(error)
        }

        return _response
      },
      async getCurrentQuestionnaireForInstitution(institutionId) {
        let _response = null

        try {
          let response = await axios.get(`/Questionnaires/Reports?institution=${institutionId}`)

          if (parseInt(response.status) >= 200 && parseInt(response.status) < 300) {
            const sentOnes = response.data.value.filter((q) => q.status === 1)

            if (sentOnes.length == 0) return null

            const { questionnaireId } = sentOnes[0]

            let questionnaires = await axios.get(`/Questionnaires/${questionnaireId}`)

            if (parseInt(questionnaires.status) >= 200 && parseInt(questionnaires.status) < 300) {
              _response = questionnaires.data
            }
          }
        } catch (error) {
          console.error(error)
        }

        return _response
      },
      async getAssetReportInBatchDetail(reportId) {
        let _response = null

        try {
          let response = await axios.get(`/Questionnaires/${reportId}`)

          if (parseInt(response.status) >= 200 && parseInt(response.status) < 300) {
            _response = response.data
          }
        } catch (error) {
          console.error(error)
        }

        return _response
      },
      async getInstitutionsForReportingPeriod(options, _callback) {
        let { reportingPeriod, type } = options

        if (!type) {
          type = 'AssetReport'
        }

        let _response = null
        let _error = null

        try {
          let response = await axios.get(`/annual-questionnaire/institutions?period=${reportingPeriod}&ReportType=${type}`)
          if (parseInt(response.status) >= 200 && parseInt(response.status) < 300) {
            _response = response.data
          }
        } catch (error) {
          _response = error
        }

        _callback(_error, _response)
      },
      async queueReportsToGenerate(options, _callback) {
        const { period, institutions } = options

        let _response = null
        let _error = null

        try {
          let response = await axios.post(`/Questionnaires/Create?period=${moment(period).format('YYYY')}&institutions=${institutions}`)

          if (parseInt(response.status) >= 200 && parseInt(response.status) < 300) {
            _response = response.data
          }
        } catch (error) {
          _response = error
        }

        _callback(_error, _response)
      },
      async previewReport(options, _callback) {
        const { batchId, reportIds, format } = options

        let _response = null
        let _error = null

        try {
          let params = new URLSearchParams()
          if (reportIds && reportIds.length > 0) {
            params.append('reportIds', reportIds.join(','))
          }
          if (format) params.append('format', format)
          let path = `/8925-reports/${batchId}/download?` + params.toString()
          return path
          // // let response = await that.axios.get(path)
          // let response = await that.axios({
          //   url: path,
          //   method: 'GET',
          //   responseType: 'blob'
          // })

          // if (parseInt(response.status) >= 200 && parseInt(response.status) < 300) {
          //   return response.data
          // }
        } catch (error) {
          _response = error
        }

        _callback(_error, _response)
      },
      async downloadReport(options, _callback) {
        const { batchId, reportIds, format } = options

        let _response = null
        let _error = null

        try {
          let params = new URLSearchParams()
          if (reportIds && reportIds.length > 0) {
            params.append('reportIds', reportIds.join(','))
          }
          if (format) params.append('format', format)
          let path = `/8925-reports/${batchId}${format === 'xlsx' ? '/xlsx' : ''}/download?` + params.toString()

          // let response = await that.axios.get(path)
          let response = await axios({
            url: path,
            method: 'GET',
            responseType: 'blob',
          })

          if (parseInt(response.status) >= 200 && parseInt(response.status) < 300) {
            const type = response.headers['content-type']
            const blob = new Blob([response.data], {
              type: type,
              encoding: 'UTF-8',
            })
            const link = document.createElement('a')
            link.href = window.URL.createObjectURL(blob)
            link.download = response.headers['content-disposition'].split('filename=')[1]
            link.click()
            _response = response.data
          }
        } catch (error) {
          _response = error
        }

        _callback(_error, _response)
      },
      async deleteReport(options, _callback) {
        const { reportId } = options

        let _response = null
        let _error = null

        try {
          let response = await axios.delete(`/annual-questionnaire/${reportId}`)

          if (parseInt(response.status) >= 200 && parseInt(response.status) < 300) {
            _response = response.data
          }
        } catch (error) {
          _response = error
        }

        _callback(_error, _response)
      },
    },
    etl: {
      async Salesforce(callback = null) {
        let result = await axios.get('/etl/Salesforce')

        if (callback) {
          callback(result)
          return
        }
        return result
      },
      async unjamSalesforceImport(callback = null) {
        let result = await axios.post('/etl/Salesforce/unjam')

        if (callback) {
          callback(result)
          return
        }
        return result
      },
      async requestSalesforceProcessing(callback = null) {
        let result = await axios.post('/etl/Salesforce')

        if (callback) {
          callback(result)
          return
        }
        return result
      },
      async newSalesforceImport(type, callback = null) {
        let result = await axios.post('/etl/Salesforce', { type })

        if (callback) {
          callback(result)
          return
        }
        return result
      },
      async unjam(callback = null) {
        let result = await axios.post('/etl/unjam')

        if (callback) {
          callback(result)
          return
        }
        return result
      },
    },
    permissions() {
      return {
        async createPermissionTemplate(permissionTemplate) {
          let response = await axios.post('/permission-templates', permissionTemplate)
          try {
            return JSON.parse(response).response
          } catch (error) {
            return response
          }
        },
        async getPermissionTemplate(permissionTemplateId) {
          let response = await axios.get(`/permission-templates/${permissionTemplateId}`)
          try {
            return JSON.parse(response).response
          } catch (error) {
            return response
          }
        },
        async deletePermissionTemplates(permissionTemplateIds) {
          let response = await axios.delete(`/permission-templates/${permissionTemplateIds}`)
          try {
            return JSON.parse(response).response
          } catch (error) {
            return response
          }
        },
        async getPermissionTemplates(take, skip) {
          let params = new URLSearchParams()
          if (!isNaN(take)) {
            params.append('take', take)
          }

          if (!isNaN(skip)) {
            params.append('skip', skip)
          }

          try {
            let req = await axios.get(`/permission-templates?${params.toString()}`)
            if (req.status >= 200 && req.status < 300) {
              return req
            }
          } catch (error) {
            return req
          }
        },
        getUserPermissions(userId) {
          let response = axios.get(`/users/${userId}/permissions`)

          try {
            return JSON.parse(response).response
          } catch (error) {
            return response
          }
        },
        setUserPermissions(userId, permissions) {
          let response = axios.put(`/users/${userId}/permissions`, permissions)

          try {
            return JSON.parse(response).response
          } catch (error) {
            return response
          }
        },
      }
    },
    async checkEmail(email) {
      let _response = null
      let _error = null

      try {
        let response = await axios.get('/users/' + btoa(email) + '/sites')

        if (typeof response === 'string') {
          response = JSON.parse(response)
        }

        if (parseInt(response.status) >= 200 && parseInt(response.status) < 300) {
          _response = {
            sites: [...response.data],
            status: parseInt(response.status),
          }
        } else {
          _response = { status: parseInt(response.status) }
        }
      } catch (error) {
        _error = error
      }

      return {
        error: _error,
        response: _response,
      }
    },
    async getInstitutionCounts(institutions) {
      let counts = []

      for (let i = 0; i < institutions.length; i++) {
        const _institution = institutions[i]

        let _response = null
        let _error = null

        try {
          let response = await axios.get(`/institutions/${_institution}/related-data`)

          if (parseInt(response.status) >= 200 && parseInt(response.status) < 300) {
            _response = response.data
          }
        } catch (error) {
          _error = error
        }

        counts.push({
          id: _institution,
          response: _response,
          error: _error,
        })
      }

      return counts
    },
    async getUserInfo(userId) {
      let _response = null

      try {
        let response = await axios.get('/users/' + userId)

        if (parseInt(response.status) === 404) {
          _response = null
        }

        if (parseInt(response.status) >= 200 && parseInt(response.status) < 300) {
          _response = response.data
        }
      } catch (error) {
        console.error(error)
      }

      return _response
    },
    async getMembershipsData(userId) {
      let _response = null

      try {
        let response = await axios.get('/memberships/' + userId)

        if (parseInt(response.status) >= 200 && parseInt(response.status) < 300) {
          _response = response.data
        }
      } catch (error) {
        console.error(error)
      }

      return _response
    },
    async getInstitutions() {
      let _response = null

      try {
        let response = await axios.get('/institutions')

        if (parseInt(response.status) >= 200 && parseInt(response.status) < 300) {
          _response = response.data
        }
      } catch (error) {
        console.error(error)
      }

      return _response
    },
    states: {
      async create(input, _callback) {
        let _response = null
        let _error = null

        try {
          let response = await axios.post('/report-configuration/states', input)

          if (parseInt(response.status) >= 200 && parseInt(response.status) < 300) {
            _response = response.data
          }
        } catch (error) {
          _error = error
        }

        _callback({
          response: _response,
          error: _error,
        })
      },
      async get(id, _callback) {
        let _response = null
        let _error = null

        try {
          let response = await axios.get('/report-configuration/states/' + id)

          if (parseInt(response.status) >= 200 && parseInt(response.status) < 300) {
            _response = response.data
          }
        } catch (error) {
          _error = error
        }

        _callback({
          response: _response,
          error: _error,
        })
      },
      async update(id, input, _callback) {
        let _response = null
        let _error = null

        try {
          let response = await axios.patch('/report-configuration/states/' + id, input)

          if (parseInt(response.status) >= 200 && parseInt(response.status) < 300) {
            _response = response.data
          }
        } catch (error) {
          _error = error
        }

        _callback({
          response: _response,
          error: _error,
        })
      },
    },
    async getStates() {
      let _response = null

      try {
        let response = await axios.get('/report-configuration/states')

        if (parseInt(response.status) >= 200 && parseInt(response.status) < 300) {
          _response = response.data
        }
      } catch (error) {
        console.error(error)
      }

      return _response
    },
    charts() {
      return {
        async getAssetSummary(clientId) {
          let _response = null

          try {
            let response = await axios.get(encodeURI(`/charts/carrier-summary?clientId=${clientId}`))

            if (parseInt(response.status) >= 200 && parseInt(response.status) < 300) {
              _response = response.data
            }
          } catch (error) {
            console.error(error)
          }

          return _response
        },
        async boli(clientId) {
          let _response = null

          try {
            let response = await axios.get(encodeURI(`/charts/boli?clientId=${clientId}`))

            if (parseInt(response.status) >= 200 && parseInt(response.status) < 300) {
              _response = response.data
            }
          } catch (error) {
            console.error(error)
          }

          return _response
        },
        async credit(clientId) {
          let _response = null

          try {
            let response = await axios.get('/charts/credit?clientId=' + clientId)

            if (parseInt(response.status) >= 200 && parseInt(response.status) < 300) {
              _response = response.data
            }
          } catch (error) {
            console.error('Error fetching credit chart data:', error)
          }

          return _response
        },
      }
    },
    async deleteMFA(userId, _callback) {
      let _response = null
      let _error = null

      try {
        let response = await axios.delete('/mfa/' + userId)

        if (parseInt(response.status) >= 200 && parseInt(response.status) < 300) {
          _response = true
        }
      } catch (error) {
        _error = error
      }

      _callback(_response, _error)
    },
    async getAnnouncements(data, _callback) {
      let _response = null
      let _error = null

      try {
        let response = await axios.post(`/announcements/search`, data)

        if (parseInt(response.status) >= 200 && parseInt(response.status) < 300) {
          _response = response.data
        }
      } catch (error) {
        _error = error
      }

      _callback(_response, _error)
    },
    // enum All: 0 | Closed: 1 | Open: 2
    async getDeathClaimsData(clientId, filter = 2) {
      let _response = null

      try {
        let response = await axios.get(`/sharepoint/lists/getByClientId?clientId=${clientId}&filter=${filter}`)

        if (parseInt(response.status) >= 200 && parseInt(response.status) < 300) {
          _response = response.data
        }
      } catch (error) {
        console.error(error)
      }

      return _response
    },
    async getActiveAnnouncements() {
      let _response = null

      try {
        let response = await axios.get(`/announcements/active`)

        if (parseInt(response.status) >= 200 && parseInt(response.status) < 300) {
          _response = response.data
        }
      } catch (error) {
        console.error(error)
      }

      return _response
    },
    async getAnnouncement(id) {
      let _response = null
      try {
        let response = await axios.get(`/announcements/${id}`)

        if (parseInt(response.status) >= 200 && parseInt(response.status) < 300) {
          _response = response.data
        }
      } catch (error) {
        console.error(error)
      }

      return _response
    },
    async deleteAnnouncement(id, _callback) {
      let _response = null
      let _error = null

      try {
        let response = await axios.delete(`/announcements/${id}`)

        if (parseInt(response.status) >= 200 && parseInt(response.status) < 300) {
          _response = response.data
        }
      } catch (error) {
        _error = error
      }

      _callback(_response, _error)
    },
    reportingPeriods: {
      async list(options) {
        const { reportEnum, take, skip } = options

        let _response = null

        try {
          let response = await axios.get(`/reports/${reportEnum}/reporting-periods`, {
            params: {
              $top: take,
              $skip: skip,
              $orderby: 'reportDate desc',
              $count: true,
            },
          })

          if (parseInt(response.status) >= 200 && parseInt(response.status) < 300) {
            _response = response.data
          }
        } catch (error) {
          console.error(error)
        }

        return _response
      },
    },
    assetReports: {
      async listAssetReportsInBatch(options) {
        const { take, skip, sort, desc, reportingPeriod, type, institutions } = options
        const reportEnum = type === 'AssetSummaryReport' ? Constants.reportEnum.assetReportSummary : Constants.reportEnum.assetReport
        let _response = null

        try {
          let response = await axios.get(`/reports/${reportEnum}/${reportingPeriod}`, {
            params: {
              $top: take,
              $skip: skip,
              $orderby: `${sort} ${desc === true || (typeof desc === 'string' && desc.includes('desc')) ? 'desc' : 'asc'}`,
              $count: true,
              institutions: institutions || undefined,
            },
          })

          if (parseInt(response.status) >= 200 && parseInt(response.status) < 300) {
            _response = response.data
          }
        } catch (error) {
          console.error(error)
        }

        return _response
      },
      async getAssetReportInBatchDetail(options) {
        const { batchId, reportId } = options

        let _response = null

        try {
          let response = await axios.get(`/asset-reports/${batchId}/reports/${reportId}`)

          if (parseInt(response.status) >= 200 && parseInt(response.status) < 300) {
            _response = response.data
          }
        } catch (error) {
          console.error(error)
        }

        return _response
      },
      async getInstitutionsForReportingPeriod(options) {
        let { reportingPeriod, type } = options

        if (!type) {
          type = 'AssetReport'
        }

        const reportEnum = type === 'AssetSummaryReport' ? Constants.reportEnum.assetReportSummary : Constants.reportEnum.assetReport
        const period = moment(reportingPeriod).format('YYYYMM')

        let _response = null

        try {
          let response = await axios.get(`/reports/${reportEnum}/${period}/unreported-institutions`)
          if (parseInt(response.status) >= 200 && parseInt(response.status) < 300) {
            _response = response.data.value
          }
        } catch (error) {
          console.error('Error fetching institutions for reporting period:', error)
          console.error(error)
        }

        return _response
      },
      async queueReportsToGenerate(options) {
        let { period, institutions, isRegenerating, type } = options

        if (!type) {
          type = 'AssetReport'
        }

        let _response = null

        try {
          let response = await axios.post('/asset-reports', {
            period,
            isRegenerating,
            institutions,
            ReportType: type,
          })

          if (parseInt(response.status) >= 200 && parseInt(response.status) < 300) {
            _response = response.data
          }
        } catch (error) {
          console.error(error)
        }

        return _response
      },
      async downloadReport(options) {
        const { batchId, reportIds, format, type } = options

        let _response = null

        try {
          let params = new URLSearchParams()
          if (reportIds && reportIds.length > 0) {
            params.append('reportIds', reportIds.join(','))
          }

          if (format) params.append('format', format)

          if (type) {
            params.append('ReportType', type)
          } else {
            params.append('ReportType', 'AssetReport')
          }

          let path = `/asset-reports/${batchId}/download?` + params.toString()

          let response = await axios({
            url: path,
            method: 'GET',
            responseType: 'blob',
          })

          if (parseInt(response.status) >= 200 && parseInt(response.status) < 300) {
            const type = response.headers['content-type']
            const blob = new Blob([response.data], {
              type: type,
              encoding: 'UTF-8',
            })
            const link = document.createElement('a')
            link.href = window.URL.createObjectURL(blob)
            link.download = response.headers['content-disposition'].split('filename=')[1]
            link.click()
            link.remove()
            _response = response.data
          }
        } catch (error) {
          console.error(error)
        }

        return _response
      },
      async deleteReport(options) {
        const { batchId, reportId } = options

        let _response = null

        try {
          let response = await axios.delete(`/asset-reports/${batchId}/reports/${reportId}`)

          if (parseInt(response.status) >= 200 && parseInt(response.status) < 300) {
            _response = response.data
          }
        } catch (error) {
          console.error(error)
        }

        return _response
      },
    },
    C8925Reports: {
      async listAssetReportsInBatch(options) {
        const { take, skip, sort, desc, reportingPeriod, reportName, clientIdWithSubsidiaryCode, clientAlternateName } = options
        let _response = null

        try {
          let response = await axios.get(`/reports/${Constants.reportEnum._8925Report}/${reportingPeriod}`, {
            params: {
              $top: take,
              $skip: skip,
              $orderby: `${sort} ${desc === true || (typeof desc === 'string' && desc.includes('desc')) ? 'desc' : 'asc'}`,
              $count: true,
              ...(reportName ? { reportName } : {}),
              ...(clientIdWithSubsidiaryCode ? { clientIdWithSubsidiaryCode } : {}),
              ...(clientAlternateName ? { clientAlternateName } : {}),
            },
          })

          if (parseInt(response.status) >= 200 && parseInt(response.status) < 300) {
            _response = response.data
          }
        } catch (error) {
          console.error(error)
        }

        return _response
      },
      async getAssetReportInBatchDetail(options) {
        const { batchId, reportId } = options

        let _response = null

        try {
          let response = await axios.get(`/8925-reports/${batchId}/reports/${reportId}`)

          if (parseInt(response.status) >= 200 && parseInt(response.status) < 300) {
            _response = response.data
          }
        } catch (error) {
          console.error(error)
        }

        return _response
      },
      async getInstitutionsForReportingPeriod(options) {
        const { reportingPeriod } = options
        const period = moment(reportingPeriod).format('YYYYMM')

        let _response = null

        try {
          let response = await axios.get(`/reports/${Constants.reportEnum._8925Report}/${period}/unreported-institutions`)
          if (parseInt(response.status) >= 200 && parseInt(response.status) < 300) {
            _response = response.data.value
          }
        } catch (error) {
          console.error(error)
        }

        return _response
      },
      async queueReportsToGenerate(options) {
        const { period, institutions, isRegenerating, reportType } = options

        let _response = null

        try {
          let response = await axios.post('/8925-reports', {
            period,
            institutions,
            isRegenerating,
            reportType,
          })

          if (parseInt(response.status) >= 200 && parseInt(response.status) < 300) {
            _response = response.data
          }
        } catch (error) {
          console.error(error)
        }

        return _response
      },
      async previewReport(options, _callback) {
        const { batchId, reportIds, format } = options

        let _response = null
        let _error = null

        try {
          let params = new URLSearchParams()
          if (reportIds && reportIds.length > 0) {
            params.append('reportIds', reportIds.join(','))
          }
          if (format) params.append('format', format)
          let path = `/8925-reports/${batchId}/download?` + params.toString()
          return path
          // // let response = await that.axios.get(path)
          // let response = await that.axios({
          //   url: path,
          //   method: 'GET',
          //   responseType: 'blob'
          // })

          // if (parseInt(response.status) >= 200 && parseInt(response.status) < 300) {
          //   return response.data
          // }
        } catch (error) {
          _response = error
        }

        _callback(_error, _response)
      },
      async downloadReport(options) {
        const { batchId, reportIds, format } = options

        let _response = null

        try {
          let params = new URLSearchParams()
          if (reportIds && reportIds.length > 0) {
            params.append('reportIds', reportIds.join(','))
          }
          if (format) params.append('format', format)
          let path = `/8925-reports/${batchId}${format === 'xlsx' ? '/xlsx' : ''}/download?` + params.toString()

          let response = await axios({
            url: path,
            method: 'GET',
            responseType: 'blob',
          })

          if (parseInt(response.status) >= 200 && parseInt(response.status) < 300) {
            const type = response.headers['content-type']
            const blob = new Blob([response.data], {
              type: type,
              encoding: 'UTF-8',
            })
            const link = document.createElement('a')
            link.href = window.URL.createObjectURL(blob)

            const fileName = response.headers['content-disposition'].split('filename=')[1]

            link.download = fileName.replaceAll('"', '')
            link.click()
            link.remove()
            _response = response.data
          }
        } catch (error) {
          console.error(error)
        }

        return _response
      },
      async deleteReport(options) {
        const { batchId, reportId } = options

        let _response = null

        try {
          let response = await axios.delete(`/8925-reports/${batchId}/reports/${reportId}`)

          if (parseInt(response.status) >= 200 && parseInt(response.status) < 300) {
            _response = response.data
          }
        } catch (error) {
          console.error(error)
        }

        return _response
      },
    },
    ...exceptionReportApi(axios),
    ...auditActionsApi(axios),
  }
}
